import {
  invalidateArticleCategoryListQuery,
  useArticleCategoryListQuery,
} from "entities/manageArticles/queries/useArticleCategoryListQuery";
import { invalidateArticleGroupListQuery } from "entities/manageArticles/queries/useArticleGroupListQuery";
import { useCreateArticleMutation } from "entities/manageArticles/queries/useCreateArticleMutation";
import { useDeleteArticleMutation } from "entities/manageArticles/queries/useDeleteArticleMutation";
import { useUpdateArticleStatusMutation } from "entities/manageArticles/queries/useUpdateArticleStatusMutation";
import {
  ArticleBrandId,
  ArticleCategory,
  ArticleGroupIds,
  ArticleStatus,
  ArticleToCreate,
} from "entities/manageArticles/types";
import { Timestamp } from "firebase/firestore";
import React, { FC, PropsWithChildren, useMemo } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { useModalState } from "shared/hooks/useModalState";
import { MANAGE_ARTICLE_DETAILS_PATH } from "shared/routes/paths";
import { createContext } from "shared/utils/context";
import { ArticleGroupPageProps, UpdatingArticleStatusParams } from "./types";

const [Provider, useArticleGroupPage] = createContext<ArticleGroupPageProps>({
  name: "ArticleGroupPageContext",
  hookName: "useArticleGroupPage",
  providerName: "ArticleGroupPageProvider",
});

const invalidateQueries = (
  brandId: ArticleBrandId,
  categoryId: ArticleCategory,
  groupId: ArticleGroupIds,
) => {
  setTimeout(() => {
    invalidateArticleGroupListQuery({ brandId, categoryId, groupId });
  }, 1000);

  setTimeout(() => {
    invalidateArticleCategoryListQuery({ brandId, categoryId });
  }, 3000);
};

const ArticleGroupPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const params = useParams();
  const brandId = params.brandId as ArticleBrandId;
  const categoryId = params.categoryId as ArticleCategory;
  const groupId = params.groupId as ArticleGroupIds;
  const navigate = useNavigate();
  const {
    isOpen: isOpenDeletingArticleModal,
    openModal: openDeletingArticleModal,
    closeModal: closeDeletingArticleModal,
    modalDetails: deletingArticleModalDetails,
  } = useModalState();

  const { data: articleListByCategory } = useArticleCategoryListQuery({
    brandId: brandId as ArticleBrandId,
    categoryId: categoryId as ArticleCategory,
  });

  const { mutateAsync: updateArticleStatus, isPending: isLoadingUpdateStatus } =
    useUpdateArticleStatusMutation();

  const {
    mutateAsync: createArticleDetails,
    isPending: isLoadingCreateArticle,
  } = useCreateArticleMutation();

  const {
    mutateAsync: deleteArticleDetails,
    isPending: isLoadingDeleteArticle,
  } = useDeleteArticleMutation();

  const articlePageTitle = useMemo(
    () =>
      articleListByCategory?.find((article) => article.id === groupId)?.name ??
      "",
    [articleListByCategory, groupId],
  );

  const onOpenDeletingArticleModal = (articleId: string) => {
    openDeletingArticleModal({ articleId });
  };

  const handleCreatingArticleDetails = async (data: ArticleToCreate) => {
    const newArticle = {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      status: ArticleStatus.UNPUBLISHED,
      content: "",
      version: "1.0.0",
    };

    await createArticleDetails(
      { brandId, categoryId, groupId, article: newArticle },
      {
        onSuccess: ({ id }) => {
          navigate(
            generatePath(MANAGE_ARTICLE_DETAILS_PATH, {
              brandId,
              categoryId,
              groupId,
              id,
            }),
          );
        },
      },
    );
  };

  const handleUpdatingArticleStatus = async (
    params: UpdatingArticleStatusParams,
  ) => {
    await updateArticleStatus(
      {
        brandId,
        categoryId,
        groupId,
        articleId: params.articleId,
        status: params.status,
        language: params.language,
      },
      { onSuccess: () => invalidateQueries(brandId, categoryId, groupId) },
    );
  };

  const handleDeletingArticleDetails = async () => {
    if (!deletingArticleModalDetails.articleId) return;

    await deleteArticleDetails(
      {
        brandId,
        categoryId,
        groupId,
        articleId: deletingArticleModalDetails.articleId,
      },
      { onSuccess: () => invalidateQueries(brandId, categoryId, groupId) },
    );
  };

  const value = {
    articlePageTitle,
    handleCreatingArticleDetails,
    handleUpdatingArticleStatus,
    handleDeletingArticleDetails,
    isLoadingCreateArticle,
    isLoadingUpdateStatus,
    isLoadingDeleteArticle,
    onOpenDeletingArticleModal,
    isOpenDeletingArticleModal,
    closeDeletingArticleModal,
    deletingArticleModalDetails,
  } as ArticleGroupPageProps;

  return <Provider value={value}>{children}</Provider>;
};

export { ArticleGroupPageProvider, useArticleGroupPage };
